import { useEffect, useRef } from 'react';

// Elementos que podem receber foco dentro de um modal.
const FOCUSABLE_SELECTOR = [
  'a[href]',
  'button:not([disabled])',
  'input:not([disabled])',
  'select:not([disabled])',
  'textarea:not([disabled])',
  '[tabindex]:not([tabindex="-1"])'
].join(', ');

/**
 * Acessibilidade de modal sem dependência externa:
 * - fecha no Escape
 * - foca o primeiro elemento interativo ao abrir
 * - prende o Tab/Shift+Tab dentro do modal
 * - devolve o foco a quem abriu, ao fechar
 *
 * Retorna a ref que deve ser aplicada no container do modal
 * (que precisa de tabIndex={-1} para poder receber foco).
 */
export function useModalAccessibility(isOpen, onClose) {
  const modalRef = useRef(null);

  // Mantém o onClose atual sem colocá-lo nas dependências do efeito —
  // os componentes recriam essa função a cada render, e isso refocaria
  // o modal continuamente.
  const onCloseRef = useRef(onClose);
  useEffect(() => {
    onCloseRef.current = onClose;
  });

  useEffect(() => {
    if (!isOpen) return undefined;

    const modal = modalRef.current;
    if (!modal) return undefined;

    const previouslyFocused = document.activeElement;
    const getFocusable = () => Array.from(modal.querySelectorAll(FOCUSABLE_SELECTOR));

    // Foca o primeiro item interativo; se não houver, o próprio container.
    (getFocusable()[0] || modal).focus();

    const handleKeyDown = (event) => {
      if (event.key === 'Escape') {
        event.preventDefault();
        onCloseRef.current();
        return;
      }

      if (event.key !== 'Tab') return;

      const focusable = getFocusable();
      if (focusable.length === 0) {
        event.preventDefault();
        modal.focus();
        return;
      }

      const first = focusable[0];
      const last = focusable[focusable.length - 1];

      // Ao chegar numa ponta, volta para a outra em vez de sair do modal.
      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    };

    document.addEventListener('keydown', handleKeyDown);

    return () => {
      document.removeEventListener('keydown', handleKeyDown);
      if (previouslyFocused instanceof HTMLElement) {
        previouslyFocused.focus();
      }
    };
  }, [isOpen]);

  return modalRef;
}
